/**
 * The last known `showWelcome` answer of each user (Ruling R19). The welcome gate holds the page
 * only while there is no answer for the user at all. A workspace switch resets the query cache, so
 * this memory keeps the page on screen. It lives in memory and in localStorage; the storage can
 * fail (a private window), so each read and write is guarded and the memory still works.
 */
const memory = new Map<number, boolean>();

function storageKey(userId: number): string {
  return `m24.onboarding.showWelcome.${userId}`;
}

export function readRememberedWelcome(userId: number | null): boolean | undefined {
  if (userId === null) {
    return undefined;
  }
  const known = memory.get(userId);
  if (known !== undefined) {
    return known;
  }
  try {
    const stored = window.localStorage.getItem(storageKey(userId));
    if (stored === 'true' || stored === 'false') {
      const value = stored === 'true';
      memory.set(userId, value);
      return value;
    }
  } catch {
    // No storage: the gate falls back to the live answer and the time cap.
  }
  return undefined;
}

export function rememberWelcome(userId: number | null, showWelcome: boolean): void {
  if (userId === null) {
    return;
  }
  memory.set(userId, showWelcome);
  try {
    window.localStorage.setItem(storageKey(userId), String(showWelcome));
  } catch {
    // No storage: the memory copy is enough for this tab.
  }
}

/** Tests only. */
export function forgetRememberedWelcome(): void {
  memory.clear();
}
